import type { MontillaPlugin } from '../types/plugin'
import { globalSingleton, __resetGlobalSingletonForTests } from '../utils/global-singleton'

const REGISTRY_KEY = 'plugin-registry'

/**
 * Pinned to `globalThis` via {@link globalSingleton} so duplicate copies of
 * `@quero/montilla-core` in the same runtime share one registry — see the
 * helper for the full rationale.
 */
const registry = globalSingleton<Map<string, MontillaPlugin>>(
  REGISTRY_KEY,
  () => new Map(),
)

/**
 * Register a plugin by name so it can be resolved later (e.g. from a
 * serialized config like `nuxt.config.ts`).
 *
 * Plugins that call this at module-level will be available as soon as
 * their package is imported — no explicit setup step needed.
 */
export function registerMontillaPlugin(plugin: MontillaPlugin): void {
  if (!plugin?.name) {
    console.warn('[montilla] Cannot register plugin without a name')
    return
  }
  registry.set(plugin.name, plugin)
}

/**
 * Resolve a previously registered plugin by its `name`.
 */
export function resolvePluginByName(name: string): MontillaPlugin | undefined {
  return registry.get(name)
}

/**
 * Return all plugins that have been registered so far.
 */
export function getRegisteredPlugins(): MontillaPlugin[] {
  return Array.from(registry.values())
}

/**
 * Test-only helper — clears the registry between tests.
 */
export function __clearPluginRegistryForTests(): void {
  registry.clear()
  __resetGlobalSingletonForTests(REGISTRY_KEY)
}
